import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useOnline } from '../hooks/useOnline';
import { DeviceLinkModal } from './DeviceLinkModal';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
  eventId?: string;
  isOrganizer?: boolean;
  nickname?: string | null;
  /** Short device identifier so attendees can share their ID. */
  deviceId?: string | null;
}

export function Layout({ children, title, eventId, isOrganizer, nickname, deviceId }: LayoutProps) {
  const isOnline = useOnline();
  const location = useLocation();
  const [showLinkModal, setShowLinkModal] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip link for keyboard users */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Header */}
      <header className="bg-white sticky top-0 z-30" style={{ boxShadow: '0 4px 4px -2px rgba(0,0,0,0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-xl font-bold text-black hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 rounded flex items-center gap-2"
              >
                <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#F7C23D' }} aria-hidden="true" />
                Barcamp Board
              </Link>
              {title && (
                <>
                  <span className="text-gray-300" aria-hidden="true">/</span>
                  <h1 className="text-lg font-medium text-gray-900 truncate">
                    {title}
                  </h1>
                </>
              )}
            </div>

            {eventId && (
              <nav className="hidden md:flex items-center gap-1" aria-label="Event navigation">
                <NavLink
                  to={`/event/${eventId}`}
                  active={location.pathname === `/event/${eventId}`}
                >
                  Overview
                </NavLink>
                <NavLink
                  to={`/event/${eventId}/proposals`}
                  active={location.pathname.includes('/proposals')}
                >
                  Session Cards
                </NavLink>
                <NavLink
                  to={`/event/${eventId}/board`}
                  active={location.pathname.includes('/board')}
                >
                  Board
                </NavLink>
                {isOrganizer && (
                  <>
                    <NavLink
                      to={`/event/${eventId}/present`}
                      active={location.pathname.includes('/present')}
                    >
                      Present
                    </NavLink>
                    <NavLink
                      to={`/event/${eventId}/admin`}
                      active={location.pathname.includes('/admin')}
                    >
                      Admin
                    </NavLink>
                  </>
                )}
              </nav>
            )}

            {/* User info, transfer & online indicator */}
            <div className="flex items-center gap-3">
              {nickname && (
                <span className="text-sm font-medium text-gray-700 hidden sm:inline truncate max-w-[160px]" title={deviceId ? `${nickname} (ID: ${deviceId.slice(-6).toUpperCase()})` : nickname}>
                  {nickname}
                  {deviceId && (
                    <span className="text-[10px] text-gray-400 ml-1 font-mono">#{deviceId.slice(-6).toUpperCase()}</span>
                  )}
                </span>
              )}
              {eventId && (
                <button
                  onClick={() => setShowLinkModal(true)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
                  title="Use on another device"
                  aria-label="Transfer to another device"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </button>
              )}
              <div className="flex items-center gap-1.5">
                <span
                  className={`w-2 h-2 rounded-full ${isOnline ? 'bg-brand-green' : 'bg-brand-gold'}`}
                  aria-hidden="true"
                />
                <span className="text-sm text-gray-500 hidden sm:inline">
                  {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile navigation */}
        {eventId && (
          <nav
            className="md:hidden overflow-x-auto"
            aria-label="Event navigation"
            style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
          >
            <div className="flex px-4 gap-1">
              <MobileNavLink
                to={`/event/${eventId}`}
                active={location.pathname === `/event/${eventId}`}
              >
                Overview
              </MobileNavLink>
              <MobileNavLink
                to={`/event/${eventId}/proposals`}
                active={location.pathname.includes('/proposals')}
              >
                Session Cards
              </MobileNavLink>
              <MobileNavLink
                to={`/event/${eventId}/board`}
                active={location.pathname.includes('/board')}
              >
                Board
              </MobileNavLink>
              {isOrganizer && (
                <>
                  <MobileNavLink
                    to={`/event/${eventId}/present`}
                    active={location.pathname.includes('/present')}
                  >
                    Present
                  </MobileNavLink>
                  <MobileNavLink
                    to={`/event/${eventId}/admin`}
                    active={location.pathname.includes('/admin')}
                  >
                    Admin
                  </MobileNavLink>
                </>
              )}
            </div>
          </nav>
        )}
      </header>

      {/* Main content */}
      <main id="main-content" className="flex-1" tabIndex={-1}>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white py-4" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          Barcamp Board - Digital Session Board with Offline Support
        </div>
      </footer>

      {/* Offline banner */}
      {!isOnline && (
        <div className="offline-banner" role="alert">
          You are offline. Changes will sync when you're back online.
        </div>
      )}

      {/* Device Link Modal (accessible from any page via top bar) */}
      <DeviceLinkModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
      />
    </div>
  );
}

function NavLink({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`px-3 py-2 text-sm font-semibold transition-colors ${
        active
          ? 'text-black'
          : 'text-gray-500 hover:text-black'
      }`}
      style={active ? { borderBottom: '2px solid #F7C23D' } : undefined}
      aria-current={active ? 'page' : undefined}
    >
      {children}
    </Link>
  );
}

function MobileNavLink({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`px-3 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
        active
          ? 'text-black'
          : 'border-transparent text-gray-500 hover:text-black'
      }`}
      style={active ? { borderBottomColor: '#F7C23D' } : undefined}
      aria-current={active ? 'page' : undefined}
    >
      {children}
    </Link>
  );
}
